import { config } from './config';
import { tokenManager } from './auth/tokenManager';
import { WishlistItemUser, Section, WishlistItemCreate, WishlistItemUpdate, ReservationCreate, UserItemInfo, ReservationHistory, UserReservation, PublicWishlist, EmailVerificationRequest, BatchItemsRequest, UserSharePreferences, UserSharePreferencesUpdate } from '../types/wishlist';

export interface CreateWishlistResponse {
  edit_link: string | null;
  share_link: string;
}

export const wishlistApi = {
  // Sections
  getSections: async (userId: number): Promise<Section[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/sections`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getDefaultSection: async (userId: number): Promise<Section> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/sections/default`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getSection: async (userId: number, sectionId: number): Promise<Section> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/sections/${sectionId}`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getFriendSections: async (friendId: number, userId: number): Promise<Section[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/friend/${friendId}/sections?user_id=${userId}`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  createSection: async (userId: number, data: { name: string; description?: string; privacy?: string }): Promise<Section> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/sections`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  updateSection: async (userId: number, sectionId: number, data: { name?: string; description?: string }): Promise<Section> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/sections/${sectionId}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  deleteSection: async (userId: number, sectionId: number): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/sections/${sectionId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  moveItem: async (userId: number, data: { item_id: number; section_id: number }): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items/move`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  // Items
  getWishlist: async (userId: number, sectionId?: number): Promise<WishlistItemUser[]> => {
    let url = `${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items`;
    if (sectionId) {
      url += `?section_id=${sectionId}`;
    }

    const response = await tokenManager.makeAuthenticatedRequest(url);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getSectionItems: async (userId: number, sectionId: number): Promise<WishlistItemUser[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/sections/${sectionId}/items`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getWishlistItem: async (userId: number, itemId: number): Promise<WishlistItemUser> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items/${itemId}`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  addItem: async (userId: number, item: WishlistItemCreate): Promise<WishlistItemUser> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items`, {
      method: 'POST',
      body: JSON.stringify(item),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  addSectionItem: async (userId: number, sectionId: number, item: WishlistItemCreate): Promise<WishlistItemUser> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/sections/${sectionId}/items`, {
      method: 'POST',
      body: JSON.stringify(item),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  updateItem: async (userId: number, itemId: number, updates: WishlistItemUpdate): Promise<WishlistItemUser> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items/${itemId}`, {
      method: 'PUT',
      body: JSON.stringify(updates),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  deleteItem: async (userId: number, itemId: number): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items/${itemId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  // Reservations
  reserveItem: async (userId: number, itemId: number, data: ReservationCreate): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/reservation/${userId}/items/${itemId}/reserve`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  unReserveItem: async (userId: number, itemId: number): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/reservation/${userId}/items/${itemId}/reserve`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  updateReservationMessage: async (userId: number, itemId: number, message: string | null): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/reservation/${userId}/items/${itemId}/reserve`, {
      method: 'PUT',
      body: JSON.stringify({
        message
      }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  checkItemReservation: async (userId: number, itemId: number): Promise<any> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/reservation/${userId}/items/${itemId}/reserve`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getUserReservations: async (userId: number): Promise<UserReservation[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/reservation/${userId}/reservations`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getReservationHistory: async (userId: number, itemId: number): Promise<ReservationHistory[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/reservation/${userId}/items/${itemId}/reservation-history`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  // Friend wishlist methods
  getFriendWishlist: async (userId: number, friendId: number, sectionId?: number): Promise<WishlistItemUser[]> => {
    let url = `${config.API_URL}/wishlist/wishlist/${userId}/wishes/friend/${friendId}`;
    if (sectionId) {
      url += `?section_id=${sectionId}`;
    }

    const response = await tokenManager.makeAuthenticatedRequest(url);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getFriendSectionItems: async (userId: number, friendId: number, sectionId: number): Promise<WishlistItemUser[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishes/friend/${friendId}/sections/${sectionId}/items`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  // Friendly aliases for legacy code
  reserveFriendItem: async (userId: number, itemId: number, reservationData: ReservationCreate): Promise<void> => {
    return wishlistApi.reserveItem(userId, itemId, reservationData);
  },

  unReserveFriendItem: async (userId: number, itemId: number): Promise<void> => {
    return wishlistApi.unReserveItem(userId, itemId);
  },

  // URL and Barcode methods
  addFromUrl: async (
    userId: number,
    url: string,
    sectionId?: number,
    fromShare: boolean = false,
  ): Promise<WishlistItemUser> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishlist/url`, {
      method: 'POST',
      body: JSON.stringify({ url, section_id: sectionId, from_share: fromShare }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  addFromBarcode: async (userId: number, barcode: string): Promise<WishlistItemUser> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishlist/barcode`, {
      method: 'POST',
      body: JSON.stringify({ barcode }),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  // Creates a wishlist item from an image shared into the native app
  // (iOS share extension / Android share intent). The server uploads the
  // image, names the item "Screenshot <date>", and applies the user's
  // saved share preferences (from_share=true), mirroring addFromUrl.
  addFromImageShare: async (userId: number, file: File): Promise<WishlistItemUser> => {
    const formData = new FormData();
    formData.append('file', file);
    formData.append('from_share', 'true');

    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items/image-share`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  // Image management methods
  uploadItemImage: async (userId: number, file: File): Promise<{ file_name: string }> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items/image`, {
      method: 'POST',
      body: formData,
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw {
        status: response.status,
        detail: errorData?.detail || errorData?.message,
        message: `Error ${response.status}: ${response.statusText}`
      };
    }

    return response.json();
  },

  updateItemImage: async (userId: number, itemId: number, filename: string, file: File): Promise<void> => {
    const formData = new FormData();
    formData.append('file', file);

    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items/${itemId}/image/${filename}`,
      {
        method: 'POST',
        body: formData,
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw {
        status: response.status,
        detail: errorData?.detail || errorData?.message,
        message: `Error ${response.status}: ${response.statusText}`
      };
    }
  },

  deleteItemImage: async (userId: number, itemId: number): Promise<void> => {
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items/${itemId}/image`,
      {
        method: 'DELETE',
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => null);
      throw {
        status: response.status,
        detail: errorData?.detail || errorData?.message,
        message: `Error ${response.status}: ${response.statusText}`
      };
    }
  },

  getAuthenticatedImage: async (userId: number, itemId: number, filename: string): Promise<string> => {
    try {
      const response = await tokenManager.makeAuthenticatedRequest(
        `${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items/${itemId}/image/${filename}`
      );

      if (!response.ok) {
        throw new Error(`Failed to load image: ${response.status}`);
      }

      const blob = await response.blob();
      return URL.createObjectURL(blob);
    } catch (error) {
      throw error;
    }
  },

  // Public wishlist methods
  createPublicWishlist: async (items: WishlistItemCreate[], email?: string | null): Promise<CreateWishlistResponse> => {
    const response = await fetch(`${config.API_URL}/wishlist/public/create_wishlist`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-token': config.API_SECRET_TOKEN
      },
      body: JSON.stringify({ items, email })
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getPublicWishlistForSharing: async (uuid: string): Promise<PublicWishlist[]> => {
    const response = await fetch(`${config.API_URL}/wishlist/public/items/share/${uuid}`, {
      headers: {
        'x-api-token': config.API_SECRET_TOKEN
      }
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  requestEmailVerification: async (uuid: string, data: EmailVerificationRequest): Promise<void> => {
    const response = await fetch(`${config.API_URL}/wishlist/public/verify-email/${uuid}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-token': config.API_SECRET_TOKEN
      },
      body: JSON.stringify(data)
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  getPublicWishlistForEditing: async (uuid: string, token: string): Promise<PublicWishlist[]> => {
    const response = await fetch(`${config.API_URL}/wishlist/public/items/edit/${uuid}`, {
      headers: {
        'x-api-token': config.API_SECRET_TOKEN,
        'X-Wishlist-Token': token
      }
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  deletePublicWishlist: async (uuid: string, token: string): Promise<void> => {
    const response = await fetch(`${config.API_URL}/wishlist/public/items/edit/${uuid}`, {
      method: 'DELETE',
      headers: {
        'x-api-token': config.API_SECRET_TOKEN,
        'X-Wishlist-Token': token
      }
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  updatePublicItem: async (uuid: string, itemId: number, item: WishlistItemCreate, token: string): Promise<void> => {
    const response = await fetch(`${config.API_URL}/wishlist/public/items/edit/${uuid}/${itemId}`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'x-api-token': config.API_SECRET_TOKEN,
        'X-Wishlist-Token': token
      },
      body: JSON.stringify(item)
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  deletePublicItem: async (uuid: string, itemId: number, token: string): Promise<void> => {
    const response = await fetch(`${config.API_URL}/wishlist/public/items/edit/${uuid}/${itemId}`, {
      method: 'DELETE',
      headers: {
        'x-api-token': config.API_SECRET_TOKEN,
        'X-Wishlist-Token': token
      }
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }
  },

  // Public sharing methods
  getPublicSections: async (userId: number): Promise<Section[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/share/user/${userId}/sections`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getPublicSection: async (userId: number, sectionId: number): Promise<Section> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/share/user/${userId}/sections/${sectionId}`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getPublicSectionItems: async (userId: number, sectionId: number): Promise<WishlistItemUser[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/share/user/${userId}/sections/${sectionId}/items`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getAllPublicItems: async (userId: number, sectionId?: number): Promise<WishlistItemUser[]> => {
    let url = `${config.API_URL}/wishlist/share/user/${userId}/items`;
    if (sectionId) {
      url += `?section_id=${sectionId}`;
    }

    const response = await tokenManager.makeAuthenticatedRequest(url);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  getPublicItem: async (userId: number, itemId: number): Promise<WishlistItemUser> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/share/user/${userId}/items/${itemId}`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  addItemsBatch: async (userId: number, data: BatchItemsRequest): Promise<WishlistItemUser[]> => {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items/batch`, {
      method: 'POST',
      body: JSON.stringify(data),
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  // Archive an item
  async archiveItem(userId: number, itemId: number): Promise<{ message: string }> {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items/${itemId}/archive`, {
      method: 'PUT',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  // Restore from archive
  async unarchiveItem(userId: number, itemId: number): Promise<{ message: string }> {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/wishlist/items/${itemId}/unarchive`, {
      method: 'PUT',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  // Get all archived items
  async getArchivedItems(userId: number): Promise<WishlistItemUser[]> {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/archived`);

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  // Permanently delete archived item
  async deleteArchivedItem(userId: number, itemId: number): Promise<{ message: string }> {
    const response = await tokenManager.makeAuthenticatedRequest(`${config.API_URL}/wishlist/wishlist/${userId}/archived/${itemId}`, {
      method: 'DELETE',
    });

    if (!response.ok) {
      const data = await response.json();
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  // Share preferences (browser/native share defaults)
  getSharePreferences: async (userId: number): Promise<UserSharePreferences> => {
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/wishlist/wishlist/${userId}/share-preferences`
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  updateSharePreferences: async (
    userId: number,
    updates: UserSharePreferencesUpdate
  ): Promise<UserSharePreferences> => {
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/wishlist/wishlist/${userId}/share-preferences`,
      {
        method: 'PUT',
        body: JSON.stringify(updates),
      }
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },

  resetSharePreferences: async (userId: number): Promise<UserSharePreferences> => {
    const response = await tokenManager.makeAuthenticatedRequest(
      `${config.API_URL}/wishlist/wishlist/${userId}/share-preferences/reset`,
      { method: 'POST' }
    );

    if (!response.ok) {
      const data = await response.json().catch(() => ({}));
      throw new Error(data.detail || data.message || response.statusText);
    }

    return response.json();
  },
};
